import { property } from "lit/decorators/property.js";
import { css, html } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import "./../atoms/title";
import "./../atoms/text";
import "./../atoms/card";
import "./../atoms/progress-spinner";
import "./auth-button";
import { when } from "lit/directives/when.js";
import { BsDialogWrapper } from "../atoms/dialog-wrapper";
import { Controller } from "../state/controller";
import { sessionStore } from "../state/session/session-store";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { loginOrSignup, openAppleOAuthFlow, openOAuthFlow, signup } from "../state/session/session-ac";
import { APIError, AuthProviderKind, DEFAULT_AUTH_PROVIDER_KINDS, EVENT_UNHANDLED_ERROR } from "shared";
import { openAcceptTerms } from "../dialogs/open-accept-terms";
import { Store } from "../state/store";
import { loading$ } from "../state/action";
import { showToast } from "../atoms/toast";
import { ICON_ERROR } from "../../icons";
import { eventService } from "../services/event-service";
import { classMap } from "lit/directives/class-map.js";
import { sessionAction } from "../state/session/session-action";
import { openAwesomeDialog } from "../util/dialog-helper";
import { SUPPORT_EMAIL } from "../config";
import { get, translate } from "lit-translate";
import { languageStore } from "../state/language/language-store";

class UserAuthStore extends Store {
	model$ = {
		sessionUser: sessionStore.model$.sessionUser,
		loading: loading$(sessionAction.login, sessionAction.signup, sessionAction.oauth)
	};
}

@customElement("bs-user-auth")
export class BsUserAuth extends BsDialogWrapper {
	static get styles() {
		return [
			...super.styles,
			css`
				#content,
				#auth-buttons-container {
					display: flex;
					flex-direction: column;
					gap: var(--space-l);
					position: relative;
				}

				.buttons {
					display: flex;
					flex-direction: column;
					gap: var(--space-m);
				}

				.buttons.hide {
					opacity: 0;
				}

				#or {
					text-align: center;
					display: flex;
					gap: var(--space-s);
					padding: 0 var(--space-l);
					color: var(--shade-dark);
				}

				#or hr {
					flex-grow: 1;
					border: 1px solid var(--shade-normal);
					border-radius: var(--space-s);
				}

				#or bs-text {
					flex-grow: 0;
				}

				#logging-in {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: var(--space-m);
					position: absolute;
					inset: 0;
				}

				#auth-buttons-container {
					position: relative;
				}
			`
		];
	}

	@property({ type: String }) headline?: string;
	@property({ type: String }) text?: string;
	@property({ type: String, attribute: "progress-text" }) progressText = translate("Signing in");
	@property({ type: Array }) kinds: AuthProviderKind[] = [...DEFAULT_AUTH_PROVIDER_KINDS, "guest"];

	protected controller = new Controller(this, new UserAuthStore(), {
		loading: (loading) => {
			const error = loading.error;
			if (error) {
				if (error instanceof APIError && error.kind === "noAccountFound") return;
				if (error.message === "User closed window" || error.message === "popup_closed_by_user") return;

				showToast(error.message, { icon: ICON_ERROR });
			}
		}
	});

	async continueWithAuthKind(kind: AuthProviderKind) {
		try {
			switch (kind) {
				case "apple":
				case "google":
				case "microsoft":
					// return redirectOAuthFlow(kind, { redirectUrl: window.location.href });
					const { credentials, data } = kind === "apple" ? await openAppleOAuthFlow() : await openOAuthFlow(kind);
					if (credentials == null) return;
					if ((await loginOrSignup(credentials, data)) != null) {
						this.done();
					}
					break;
				case "guest":
					if (!(await openAcceptTerms())) return;
					await signup({ kind: "guest" }, { data: { language: languageStore.currentLanguage } });
					this.done();
					break;
			}
		} catch (err) {
			// Ignore if the user closed the window
			if (err.message === "popup_closed_by_user" || err.error === "popup_closed_by_user") {
				return;
			}

			// This is the only way I found of checking if the user denied access to the scope
			if (err.message.includes("denied access to the scope")) {
				eventService.trackEvent(EVENT_UNHANDLED_ERROR(new Error(`failed_to_sign_in.access_denied.${kind}`)));
				openAwesomeDialog({
					title: get("Could not sign in"),
					icon: "🚪",
					color: "#ffc2a1",
					text: get(
						`You need to allow Ideamap to access your account to sign in. Please try again and allow access. Contact our support team at {{email}} if the issue persists.`,
						{ email: SUPPORT_EMAIL }
					)
				});
			} else {
				eventService.trackEvent(EVENT_UNHANDLED_ERROR(new Error(`failed_to_sign_in.${kind}`)));
				openAwesomeDialog({
					title: get("Could not sign in"),
					icon: "🚪",
					color: "#ffc2a1",
					text: get(
						`Something unexpected went wrong while signing in. Please contact our support team at {{email}} if the issue persists and provide the following error message: "{{message}}"`,
						{ email: SUPPORT_EMAIL, message: err.toString() || get("unknown sign in error") }
					)
				});
			}
		}
	}

	render() {
		const { sessionUser, loading } = this.controller.model;
		const kindsWithoutGuest = this.kinds.filter((kind) => kind !== "guest");
		return html`
			<header id="header">
				${when(this.headline != null, () => html`<bs-title margin size="large">${this.headline}</bs-title>`)}
				${when(this.text != null, () => html`<bs-text size="large" label>${this.text}</bs-text>`)}
			</header>
			<article id="content">
				<div id="auth-buttons-container">
					<div class="${classMap({ buttons: true, hide: loading?.start ?? false })}">
						${repeat(
							kindsWithoutGuest,
							(kind) => html`
								<bs-auth-button
									kind="${kind}"
									size="large"
									@click="${() => this.continueWithAuthKind(kind)}"
								></bs-auth-button>
							`
						)}
					</div>
					${when(
						this.kinds.includes("guest"),
						() => html`
							<div id="or">
								<hr />
								<bs-text size="small">${translate(/*used in the context: ... OR ...*/ `OR`)}</bs-text>
								<hr />
							</div>
							<div class="buttons">
								<bs-auth-button
									kind="guest"
									size="large"
									.user="${ifDefined(sessionUser)}"
									@click="${() => this.continueWithAuthKind("guest")}"
								></bs-auth-button>
							</div>
						`
					)}
					${when(
						loading?.start,
						() => html`
							<bs-card id="logging-in">
								<bs-progress-spinner></bs-progress-spinner>
								<bs-text>${this.progressText}...</bs-text>
							</bs-card>
						`
					)}
				</div>
				<slot></slot>
			</article>
		`;
	}
}
